const SCALE_ALIASES = {
  "1min": "1m",
  "2min": "2m",
  "3min": "3m",
  "5min": "5m",
  "1day": "1d",
};

const CHART_TIME_ZONE = "America/Los_Angeles";
const US_EQUITY_MARKET_TIME_ZONE = "America/New_York";

const SCALE_TO_MINUTES = {
  "1m": 1,
  "2m": 2,
  "3m": 3,
  "5m": 5,
  "10m": 10,
  "15m": 15,
  "30m": 30,
  "1h": 60,
  "2h": 120,
  "3h": 180,
  "4h": 240,
};

const DAILY_LIKE_SCALES = new Set(["1d", "1w", "1mo"]);
const US_EQUITY_LIKE_ASSET_TYPES = new Set(["stocks", "indices", "funds"]);
const US_EQUITY_PREMARKET_START_MINUTES = 4 * 60;
const US_EQUITY_REGULAR_OPEN_MINUTES = 9 * 60 + 30;
const US_EQUITY_REGULAR_CLOSE_MINUTES = 16 * 60;
const US_EQUITY_EARLY_CLOSE_MINUTES = 13 * 60;
const US_EQUITY_OVERNIGHT_START_MINUTES = 20 * 60;
const TIME_ZONE_ABBREVIATION_OFFSETS = {
  UTC: "+00:00",
  GMT: "+00:00",
  EST: "-05:00",
  EDT: "-04:00",
  CST: "-06:00",
  CDT: "-05:00",
  MST: "-07:00",
  MDT: "-06:00",
  PST: "-08:00",
  PDT: "-07:00",
};

const pad2 = (value) => String(value).padStart(2, "0");

const zonedPartsFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: CHART_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

export const normalizeLiveChartScale = (scale) =>
  SCALE_ALIASES[String(scale || "").trim()] || String(scale || "").trim() || "1d";

const parseUtcDate = (raw) => {
  if (!raw) return null;

  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : new Date(raw.getTime());
  }

  if (typeof raw === "number") {
    const date = new Date(raw < 1e12 ? raw * 1000 : raw);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const value = String(raw).trim();
  if (!value) return null;

  const naiveMatch = value.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/
  );
  if (naiveMatch) {
    const [, year, month, day, hour, minute, second] = naiveMatch;
    return new Date(
      Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second || "0"),
        0
      )
    );
  }

  const absolute = new Date(value);
  if (Number.isNaN(absolute.getTime())) return null;

  const parts = zonedPartsFormatter.formatToParts(absolute);
  const get = (type) => parts.find((part) => part.type === type)?.value || null;
  const year = Number(get("year"));
  const month = Number(get("month"));
  const day = Number(get("day"));
  const hour = Number(get("hour"));
  const minute = Number(get("minute"));
  const second = Number(get("second"));

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute) ||
    !Number.isFinite(second)
  ) {
    return null;
  }

  return new Date(Date.UTC(year, month - 1, day, hour, minute, second, 0));
};

const formatUtcDatetime = (date) =>
  `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())} ${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}:${pad2(date.getUTCSeconds())}`;

const parseAbsoluteDate = (raw) => {
  if (!raw) return null;

  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : new Date(raw.getTime());
  }

  if (typeof raw === "number") {
    const date = new Date(raw < 1e12 ? raw * 1000 : raw);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const value = String(raw).trim();
  if (!value) return null;

  const displayTimeMatch = value.match(
    /^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)\s+([A-Z]{2,4})$/i
  );
  if (displayTimeMatch) {
    const [, year, month, day, rawHour, minute, second, meridiem, zone] =
      displayTimeMatch;
    const offset = TIME_ZONE_ABBREVIATION_OFFSETS[zone.toUpperCase()];
    if (offset) {
      let hour = Number(rawHour);
      if (meridiem.toUpperCase() === "PM" && hour !== 12) hour += 12;
      if (meridiem.toUpperCase() === "AM" && hour === 12) hour = 0;
      const date = new Date(
        `${year}-${month}-${day}T${pad2(hour)}:${minute}:${second || "00"}${offset}`
      );
      if (!Number.isNaN(date.getTime())) return date;
    }
  }

  const absolute = new Date(value);
  return Number.isNaN(absolute.getTime()) ? null : absolute;
};

const usEquityPartsFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: US_EQUITY_MARKET_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  weekday: "short",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

const usMarketHolidayCache = new Map();
const usMarketEarlyCloseCache = new Map();

const dateKey = (year, month, day) =>
  `${year}-${pad2(month)}-${pad2(day)}`;

const utcDate = (year, month, day) =>
  new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0));

const isWeekendDate = (year, month, day) => {
  const weekday = utcDate(year, month, day).getUTCDay();
  return weekday === 0 || weekday === 6;
};

const observedNewYearsDay = (year) => {
  const date = utcDate(year, 1, 1);
  const weekday = date.getUTCDay();
  if (weekday === 0) return dateKey(year, 1, 2);
  if (weekday === 6) return null;
  return dateKey(year, 1, 1);
};

const observedFixedHoliday = (year, month, day) => {
  const date = utcDate(year, month, day);
  const weekday = date.getUTCDay();
  if (weekday === 6) {
    const observed = new Date(date.getTime() - 24 * 60 * 60 * 1000);
    return dateKey(observed.getUTCFullYear(), observed.getUTCMonth() + 1, observed.getUTCDate());
  }
  if (weekday === 0) {
    const observed = new Date(date.getTime() + 24 * 60 * 60 * 1000);
    return dateKey(observed.getUTCFullYear(), observed.getUTCMonth() + 1, observed.getUTCDate());
  }
  return dateKey(year, month, day);
};

const nthWeekday = (year, month, weekday, occurrence) => {
  const date = utcDate(year, month, 1);
  while (date.getUTCDay() !== weekday) {
    date.setUTCDate(date.getUTCDate() + 1);
  }
  date.setUTCDate(date.getUTCDate() + ((occurrence - 1) * 7));
  return dateKey(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
};

const lastWeekday = (year, month, weekday) => {
  const date = utcDate(year, month + 1, 0);
  while (date.getUTCDay() !== weekday) {
    date.setUTCDate(date.getUTCDate() - 1);
  }
  return dateKey(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
};

const easterSunday = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = ((19 * a) + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + (2 * e) + (2 * i) - h - k) % 7;
  const m = Math.floor((a + (11 * h) + (22 * l)) / 451);
  const month = Math.floor((h + l - (7 * m) + 114) / 31);
  const day = ((h + l - (7 * m) + 114) % 31) + 1;
  return utcDate(year, month, day);
};

const goodFriday = (year) => {
  const date = easterSunday(year);
  date.setUTCDate(date.getUTCDate() - 2);
  return dateKey(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
};

const usMarketHolidayKeysForYear = (year) => {
  if (!usMarketHolidayCache.has(year)) {
    usMarketHolidayCache.set(
      year,
      new Set([
        observedNewYearsDay(year),
        nthWeekday(year, 1, 1, 3),
        nthWeekday(year, 2, 1, 3),
        goodFriday(year),
        lastWeekday(year, 5, 1),
        observedFixedHoliday(year, 6, 19),
        observedFixedHoliday(year, 7, 4),
        nthWeekday(year, 9, 1, 1),
        nthWeekday(year, 11, 4, 4),
        observedFixedHoliday(year, 12, 25),
      ].filter(Boolean)),
    );
  }
  return usMarketHolidayCache.get(year);
};

const usMarketEarlyCloseKeysForYear = (year) => {
  if (!usMarketEarlyCloseCache.has(year)) {
    const holidayKeys = usMarketHolidayKeysForYear(year);
    const keys = [
      dateKey(year, 7, 3),
      nthWeekday(year, 11, 5, 4),
      dateKey(year, 12, 24),
    ].filter((key) => {
      if (holidayKeys.has(key)) return false;
      const [rawYear, rawMonth, rawDay] = key.split("-").map(Number);
      return !isWeekendDate(rawYear, rawMonth, rawDay);
    });
    usMarketEarlyCloseCache.set(year, new Set(keys));
  }
  return usMarketEarlyCloseCache.get(year);
};

export const usEquitySessionForQuote = (rawDate = null) => {
  const absolute = parseAbsoluteDate(rawDate) || new Date();
  if (Number.isNaN(absolute.getTime())) return "unknown";

  const parts = usEquityPartsFormatter.formatToParts(absolute);
  const get = (type) => parts.find((part) => part.type === type)?.value || null;
  const weekday = get("weekday");
  if (weekday === "Sat" || weekday === "Sun") return "weekend";

  const year = Number(get("year"));
  const month = Number(get("month"));
  const day = Number(get("day"));
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return "unknown";
  }

  const key = dateKey(year, month, day);
  if (usMarketHolidayKeysForYear(year).has(key)) return "holiday";

  const hour = Number(get("hour"));
  const minute = Number(get("minute"));
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return "unknown";

  const closeMinutes = usMarketEarlyCloseKeysForYear(year).has(key)
    ? US_EQUITY_EARLY_CLOSE_MINUTES
    : US_EQUITY_REGULAR_CLOSE_MINUTES;
  const minutes = hour * 60 + minute;
  if (
    minutes < US_EQUITY_PREMARKET_START_MINUTES ||
    minutes >= US_EQUITY_OVERNIGHT_START_MINUTES
  ) return "overnight";
  if (minutes < US_EQUITY_REGULAR_OPEN_MINUTES) return "premarket";
  if (minutes >= closeMinutes) return "afterhours";
  return "regular";
};

export const extendedHoursLabelForQuote = (rawDate = null) => {
  const session = usEquitySessionForQuote(rawDate);
  if (session === "overnight") return "Overnight";
  if (session === "premarket") return "Premarket";
  if (session === "holiday") return "Market closed";
  if (session === "afterhours" || session === "weekend") return "After hours";
  return "Extended hours";
};

export const pricePctChangeFieldForQuote = ({
  quoteAtIso = null,
  assetType = null,
} = {}) => {
  const normalizedAssetType = String(assetType || "").trim().toLowerCase();
  const equityLike =
    !normalizedAssetType || US_EQUITY_LIKE_ASSET_TYPES.has(normalizedAssetType);
  if (!equityLike) return "pricePctChange1d";
  if (!quoteAtIso) return "pricePctChange1d";

  const session = usEquitySessionForQuote(quoteAtIso);
  if (
    session === "overnight" ||
    session === "premarket" ||
    session === "afterhours" ||
    session === "weekend" ||
    session === "holiday"
  ) {
    return "extendedHoursPctChange";
  }

  return "pricePctChange1d";
};

const priceChangeFieldForPctField = (pctChangeField) =>
  pctChangeField === "extendedHoursPctChange"
    ? "extendedHoursPriceChange"
    : "priceChange1d";

const referenceFieldForPctField = (pctChangeField) =>
  pctChangeField === "extendedHoursPctChange"
    ? "extendedHoursReferencePrice"
    : "priceChangeReferencePrice";

const toQuoteNumber = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const cleaned = String(value).replace(/[%$,]/g, "").trim();
  if (!cleaned) return null;

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const firstFiniteNumber = (...values) => {
  for (const value of values) {
    if (Number.isFinite(value)) return value;
  }
  return null;
};

export const buildLiveQuoteMoveUpdate = ({
  currentInfo = {},
  nextPrice,
  quoteAtIso = null,
  assetType = null,
  pctChangeValue = null,
  priceChangeValue = null,
  includeNullFields = false,
} = {}) => {
  const livePrice = toQuoteNumber(nextPrice);
  if (livePrice == null) return {};

  const pctChangeField = pricePctChangeFieldForQuote({ quoteAtIso, assetType });
  const priceChangeField = priceChangeFieldForPctField(pctChangeField);
  const referenceField = referenceFieldForPctField(pctChangeField);

  const payloadPriceChange = toQuoteNumber(priceChangeValue);
  const payloadPctChange = toQuoteNumber(pctChangeValue);
  const currentPrice = toQuoteNumber(
    currentInfo?.currentPriceValue ?? currentInfo?.currentPrice,
  );
  const currentPriceChange = toQuoteNumber(currentInfo?.[priceChangeField]);
  const currentPctChange = toQuoteNumber(currentInfo?.[pctChangeField]);
  const currentReference = toQuoteNumber(currentInfo?.[referenceField]);
  const extendedHoursCloseReference =
    pctChangeField === "extendedHoursPctChange"
      ? toQuoteNumber(currentInfo?.closePriceValue ?? currentInfo?.closePrice)
      : null;

  const referenceFromPayloadPriceChange =
    payloadPriceChange != null ? livePrice - payloadPriceChange : null;
  const referenceFromPayloadPct =
    payloadPctChange != null && payloadPctChange !== -100
      ? livePrice / (1 + payloadPctChange / 100)
      : null;
  const referenceFromCurrentPriceChange =
    currentPrice != null && currentPriceChange != null
      ? currentPrice - currentPriceChange
      : null;
  const referenceFromCurrentPct =
    currentPrice != null && currentPctChange != null && currentPctChange !== -100
      ? currentPrice / (1 + currentPctChange / 100)
      : null;
  const referencePrice = firstFiniteNumber(
    referenceFromPayloadPriceChange,
    currentReference,
    extendedHoursCloseReference,
    referenceFromCurrentPriceChange,
    referenceFromPayloadPct,
    referenceFromCurrentPct,
  );

  const nextPriceChange =
    payloadPriceChange != null
      ? payloadPriceChange
      : referencePrice != null
        ? livePrice - referencePrice
        : null;
  const nextPctChange =
    payloadPctChange != null
      ? payloadPctChange
      : referencePrice != null && referencePrice !== 0
        ? ((livePrice / referencePrice) - 1) * 100
        : null;

  const fields = {};
  if (nextPriceChange != null || includeNullFields) {
    fields[priceChangeField] = nextPriceChange;
  }
  if (nextPctChange != null || includeNullFields) {
    fields[pctChangeField] = nextPctChange;
  }
  if (referencePrice != null) fields[referenceField] = referencePrice;
  return fields;
};

const shouldMergeQuoteIntoScale = ({ scale, assetType, quoteAtIso }) => {
  const normalizedScale = normalizeLiveChartScale(scale);
  if (!DAILY_LIKE_SCALES.has(normalizedScale)) return true;

  const normalizedAssetType = String(assetType || "").trim().toLowerCase();
  const equityLike =
    !normalizedAssetType || US_EQUITY_LIKE_ASSET_TYPES.has(normalizedAssetType);
  if (!equityLike) return true;

  return usEquitySessionForQuote(quoteAtIso) === "regular";
};

const bucketStartFromDate = (date, rawScale) => {
  if (!date) return null;

  const scale = normalizeLiveChartScale(rawScale);
  const bucketMinutes = SCALE_TO_MINUTES[scale];
  if (bucketMinutes) {
    const minutes = date.getUTCMinutes();
    const bucketMinute = Math.floor(minutes / bucketMinutes) * bucketMinutes;
    return new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        bucketMinute,
        0,
        0
      )
    );
  }

  if (scale === "1d") {
    return new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0)
    );
  }

  if (scale === "1w") {
    const weekday = date.getUTCDay();
    const diff = weekday === 0 ? -6 : 1 - weekday;
    return new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate() + diff,
        0,
        0,
        0,
        0
      )
    );
  }

  if (scale === "1mo") {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 0, 0, 0, 0));
  }

  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0)
  );
};

const bucketStartUtc = (rawDate, rawScale) => {
  const date = parseUtcDate(rawDate);
  return bucketStartFromDate(date, rawScale);
};

const addBucketStep = (date, rawScale) => {
  const scale = normalizeLiveChartScale(rawScale);
  const bucketMinutes = SCALE_TO_MINUTES[scale];
  if (bucketMinutes) {
    return new Date(date.getTime() + bucketMinutes * 60 * 1000);
  }
  if (scale === "1d") return new Date(date.getTime() + 24 * 60 * 60 * 1000);
  if (scale === "1w") return new Date(date.getTime() + 7 * 24 * 60 * 60 * 1000);
  if (scale === "1mo") {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1, 0, 0, 0, 0));
  }
  return new Date(date.getTime() + 24 * 60 * 60 * 1000);
};

const toFiniteNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const resolveQuoteBucketDate = ({ quoteAtIso, scale, lastBarDatetime }) => {
  const normalizedBucket = bucketStartUtc(quoteAtIso, scale);
  const absoluteBucket = bucketStartFromDate(parseAbsoluteDate(quoteAtIso), scale);

  if (!lastBarDatetime) {
    return normalizedBucket || absoluteBucket;
  }

  const lastBucketDate = bucketStartUtc(lastBarDatetime, scale);
  if (!lastBucketDate) {
    return normalizedBucket || absoluteBucket;
  }

  const candidates = [normalizedBucket, absoluteBucket]
    .filter((date) => date instanceof Date && !Number.isNaN(date.getTime()))
    .map((date) => ({
      date,
      diffMs: date.getTime() - lastBucketDate.getTime(),
    }));

  if (candidates.length === 0) return null;

  const nextForward = candidates
    .filter((candidate) => candidate.diffMs >= 0)
    .sort((a, b) => a.diffMs - b.diffMs)[0];

  if (nextForward) {
    return nextForward.date;
  }

  return candidates.sort(
    (a, b) => Math.abs(a.diffMs) - Math.abs(b.diffMs)
  )[0].date;
};

export const mergeQuoteIntoSeries = ({
  series,
  scale,
  price,
  quoteAtIso,
  assetType = null,
}) => {
  if (!Array.isArray(series) || series.length === 0) return series;

  const livePrice = toFiniteNumber(price);
  if (livePrice == null) return series;
  if (!shouldMergeQuoteIntoScale({ scale, assetType, quoteAtIso })) return series;

  const lastBar = series[series.length - 1];
  const bucketDate = resolveQuoteBucketDate({
    quoteAtIso,
    scale,
    lastBarDatetime: lastBar?.datetime,
  });
  if (!bucketDate) return series;

  const lastBucketDate = bucketStartUtc(lastBar?.datetime, scale);
  if (!lastBucketDate) return series;
  const nowBucketDate = bucketStartFromDate(new Date(), scale);
  const maxAllowedBucketDate = nowBucketDate
    ? addBucketStep(nowBucketDate, scale)
    : null;

  if (
    maxAllowedBucketDate &&
    bucketDate.getTime() > maxAllowedBucketDate.getTime()
  ) {
    return series;
  }

  const targetDatetime = formatUtcDatetime(bucketDate);
  const lastDatetime = formatUtcDatetime(lastBucketDate);

  if (targetDatetime < lastDatetime) {
    return series;
  }

  if (targetDatetime === lastDatetime) {
    const nextLast = {
      ...lastBar,
      open: toFiniteNumber(lastBar.open) ?? livePrice,
      high: Math.max(toFiniteNumber(lastBar.high) ?? livePrice, livePrice),
      low: Math.min(toFiniteNumber(lastBar.low) ?? livePrice, livePrice),
      close: livePrice,
    };
    return series.slice(0, -1).concat(nextLast);
  }

  const previousClose =
    toFiniteNumber(lastBar.close) ??
    toFiniteNumber(lastBar.open) ??
    livePrice;

  return series.concat({
    datetime: targetDatetime,
    open: previousClose,
    high: Math.max(previousClose, livePrice),
    low: Math.min(previousClose, livePrice),
    close: livePrice,
    volume: 0,
    vwap:
      lastBar && lastBar.vwap != null && Number.isFinite(Number(lastBar.vwap))
        ? Number(lastBar.vwap)
        : null,
  });
};

export const mergeQuoteIntoSeriesMap = ({
  seriesMap,
  tickerId,
  idx,
  price,
  quoteAtIso,
  assetType = null,
}) => {
  if (!seriesMap || typeof seriesMap !== "object") return seriesMap;
  if (tickerId == null || idx == null) return seriesMap;

  const prefix = `${String(tickerId)}-`;
  const suffix = `-${String(idx)}`;
  let changed = false;
  const nextMap = { ...seriesMap };

  Object.keys(seriesMap).forEach((key) => {
    if (!key.startsWith(prefix) || !key.endsWith(suffix)) return;
    const scale = key.slice(prefix.length, key.length - suffix.length);
    const currentSeries = seriesMap[key];
    const nextSeries = mergeQuoteIntoSeries({
      series: currentSeries,
      scale,
      price,
      quoteAtIso,
      assetType,
    });

    if (nextSeries !== currentSeries) {
      nextMap[key] = nextSeries;
      changed = true;
    }
  });

  return changed ? nextMap : seriesMap;
};
